import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/book";
import { useAuth } from "@/lib/auth";

/**
 * Mounts at /admin/profile (the redirect target from Emergent Auth).
 * Reads session_id from URL fragment, exchanges it with backend, then
 * routes the user to /admin or /login on failure.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Guard against StrictMode double-invoke — session_id is single-use.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      const hash = window.location.hash || "";
      const m = hash.match(/session_id=([^&]+)/);
      if (!m) {
        navigate("/login?error=missing_session", { replace: true });
        return;
      }
      const session_id = decodeURIComponent(m[1]);
      // Clear the hash immediately so it can't be replayed
      window.history.replaceState({}, "", "/admin/profile");
      try {
        await axios.post(
          `${API}/auth/session`,
          { session_id },
          { withCredentials: true }
        );
        await refresh();
        navigate("/admin", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "auth_failed";
        navigate(`/login?error=${encodeURIComponent(msg)}`, { replace: true });
      }
    };
    run();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#D92D20]" />
        <div className="text-sm uppercase tracking-[0.25em] text-[#525252]">Signing you in…</div>
      </div>
    </div>
  );
}
